/**
 * StarterGate — route guard that swaps the wrapped page for an
 * upgrade-prompt placeholder when the active workspace is on the
 * Starter (or paused) plan.
 *
 * BRIEF-plan-tiered-ux PR2 — defence-in-depth for direct-URL pastes
 * after PR1 dropped these routes from the sidebar. Starter customers
 * who navigate by typing/bookmark land on a friendly upgrade card
 * instead of a feature surface they can't use.
 *
 * The Pro/Trial/Agency path renders children unchanged. While billing
 * status is loading the gate renders `null` (same pattern as
 * `OwnerOnlyRoute`) so the wrapped page never sees a flicker on
 * refresh.
 */
import { useWorkspaceContext } from './WorkspaceProvider';
import { useBillingStatus } from '@/hooks';
import { EmptyFeatureCard } from './ui/EmptyFeatureCard';

interface StarterGateProps {
  children: React.ReactNode;
  /** i18n key for the page title shown in the upgrade card. */
  titleKey: string;
  /** i18n key for the upgrade-prompt body copy. */
  descriptionKey: string;
}

export function StarterGate({ children, titleKey, descriptionKey }: StarterGateProps) {
  const { activeWorkspace, isLoading } = useWorkspaceContext();
  const { data: billing, isPending } = useBillingStatus(activeWorkspace?.id ?? '');

  if (isLoading || !activeWorkspace || isPending) return null;

  const isStarter = billing?.plan === 'starter' || billing?.plan === 'paused';
  if (isStarter) return <EmptyFeatureCard titleKey={titleKey} descriptionKey={descriptionKey} />;

  return <>{children}</>;
}
